import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { trackDesignSystemAction } from 'analytics';
import { CATEGORY_COMMENT_MAP, DesignSystemSection, TokenCategory } from 'designSystem/types';
import { DS_TAB_CHROME } from 'designSystem/config';
import {
	dsPickToNavTarget,
	getTokenCategoryFromPick,
	symbolDisplayName,
} from 'designSystem/content/util';
import type { DesignSystemContent } from 'designSystem/content/types';
import type { DsPick } from 'dragonmouse-shared/interfaces';
import {
	addDsTab,
	setActiveDsTab,
	setDesignSystemFilterCategories,
	updateDsTab,
} from 'redux/mode/actions';
import { displayAlertModal } from 'redux/mode/util';
import store from 'redux/store';

const SECTION_LEVEL_ONLY = new Set<DesignSystemSection>(['theme', 'styles', 'foundations']);

const VALID_THEME_CATEGORIES = new Set<string>(Object.values(CATEGORY_COMMENT_MAP));

// Derives a tab label from section + contentPath by looking up the content
function resolveTabLabel(
	section: DesignSystemSection,
	contentPath: string | undefined,
	content: DesignSystemContent | null
): string {
	const chrome = DS_TAB_CHROME[section];
	if (!contentPath) return chrome?.label ?? section;

	if (section === 'components') {
		return content?.components?.byId?.[contentPath]?.name ?? symbolDisplayName(contentPath);
	}
	if (section === 'guidelines') {
		const guideline = content?.guidelines?.find((g) => g.path === contentPath);
		return guideline?.title ?? symbolDisplayName(contentPath);
	}
	if (section === 'gallery') {
		const gallery = content?.gallery?.find((g) => g.path === contentPath);
		return gallery?.title ?? symbolDisplayName(contentPath);
	}

	return chrome?.label ?? section;
}

// Checks whether a detail-tab content item exists in the current DS content
function contentExists(
	section: DesignSystemSection,
	contentPath: string,
	content: DesignSystemContent | null
): boolean {
	if (section === 'components') return !!content?.components?.byId?.[contentPath];
	if (section === 'guidelines') return !!content?.guidelines?.find((g) => g.path === contentPath);
	if (section === 'gallery') return !!content?.gallery?.find((g) => g.path === contentPath);
	return true;
}

// Centralizes DS tab navigation: tab creation/activation,
// assets single-tab convention, and theme category filtering.
const useDsNavigation = () => {
	const dispatch = useDispatch();

	// Opens or activates a DS tab. When background is true, the tab is
	// created but the current active tab is preserved (Cmd-click behaviour).
	const openTab = useCallback(
		(section: DesignSystemSection, contentPath?: string, background?: boolean) => {
			console.log('openTab', section, contentPath, background);
			trackDesignSystemAction('navigate_section', { section, contentPath });
			const chrome = DS_TAB_CHROME[section];
			if (!chrome) return;

			const state = store.getState();
			const dsTabs = state.mode.dsTabs;
			const previousActiveTabId = state.mode.activeDsTabId;

			if (section === 'assets') {
				const tabId = 'section-assets';
				const existingTab = dsTabs.find((t) => t.id === tabId);
				if (existingTab) {
					if (contentPath) dispatch(updateDsTab({ id: tabId, contentPath }));
					if (!background) dispatch(setActiveDsTab(tabId));
				} else {
					dispatch(addDsTab({ id: tabId, section, contentPath, label: chrome.label }));
					if (background) dispatch(setActiveDsTab(previousActiveTabId));
				}
			} else if (SECTION_LEVEL_ONLY.has(section)) {
				const tabId = `section-${section}`;
				const existingTab = dsTabs.find((t) => t.id === tabId);
				if (existingTab) {
					if (!background) dispatch(setActiveDsTab(tabId));
				} else {
					dispatch(addDsTab({ id: tabId, section, label: chrome.label }));
					if (background) dispatch(setActiveDsTab(previousActiveTabId));
				}
				if (
					section === 'theme' &&
					contentPath &&
					VALID_THEME_CATEGORIES.has(contentPath as TokenCategory)
				) {
					dispatch(
						setDesignSystemFilterCategories({
							section: 'theme',
							categories: [contentPath as TokenCategory],
						})
					);
				}
			} else {
				const tabId = contentPath ? `${section}-${contentPath}` : `section-${section}`;
				const content = state.mode.designSystemContent;
				const existingTab = dsTabs.find((t) => t.id === tabId);
				if (existingTab) {
					if (!background) dispatch(setActiveDsTab(tabId));
				} else if (contentPath && !contentExists(section, contentPath, content)) {
					displayAlertModal({
						title: 'Item not found',
						description:
							'This item was renamed or removed from the design system and is no longer available.',
						submitLabel: 'OK',
						onSubmit: () => {},
					});
					return;
				} else {
					dispatch(
						addDsTab({
							id: tabId,
							section,
							contentPath,
							label: resolveTabLabel(section, contentPath, content),
							showBackButton: !!contentPath,
						})
					);
					if (background) dispatch(setActiveDsTab(previousActiveTabId));
				}
			}
		},
		[dispatch]
	);

	// Opens (or activates) a tab to show the provided DsPick
	const openPickTab = useCallback(
		(pick: DsPick) => {
			const target = dsPickToNavTarget(pick);
			if (!target) return;

			openTab(target.section, target.contentPath);

			if (pick.kind === 'token') {
				const content = store.getState().mode.designSystemContent;
				const category = getTokenCategoryFromPick(pick.cssVariable, content);
				if (category) {
					dispatch(
						setDesignSystemFilterCategories({
							section: 'theme',
							categories: [category],
						})
					);
				}
			}
		},
		[openTab, dispatch]
	);

	return { openTab, openPickTab };
};

export default useDsNavigation;
